import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import baseUrl from "../../../../../api/baseUrl";
import { RootState } from "../../../../../redux/store";
import {
  addPastHistoryItem,
  removePastHistoryItem,
} from "../../../../../redux/diagnosestate/diagnoseAction";

export type PastHistoryType =
  | "extraction"
  | "restoration"
  | "endodontic"
  | "prosthodontic"
  | "scaling"
  | "other";

interface FormData {
  type: PastHistoryType | "";
  note: string;
}

const PAST_HISTORY_OPTIONS: { value: PastHistoryType; label: string }[] = [
  { value: "extraction", label: "Extraction" },
  { value: "restoration", label: "Restoration" },
  { value: "endodontic", label: "Endodontic" },
  { value: "prosthodontic", label: "Prosthodontic" },
  { value: "scaling", label: "Scaling" },
  { value: "other", label: "Other" },
];

function PastHistory({ setmakeAction, makeAction }: { setmakeAction?: (v: boolean) => void; makeAction?: boolean }) {
  const dispatch = useDispatch();
  const { ID } = useParams();
  const pastHistory = useSelector((state: RootState) => state.diagnose.pastHistory ?? []);

  const [formData, setFormData] = useState<FormData>({
    type: "",
    note: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.type || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const response = await baseUrl.post(
        "treatment/add-past-history",
        { type: formData.type, note: formData.note || "" },
        { headers: { treatmentid: ID } }
      );
      dispatch(addPastHistoryItem(response.data.data));
      setFormData({ type: "", note: "" });
      if (setmakeAction) setmakeAction(!makeAction);
    } catch (error) {
      console.error(error);
      toast.error("Failed to add past history.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemove = async (item: { _id: string }) => {
    try {
      await baseUrl.post("treatment/remove-past-history", {}, {
        headers: { treatmentid: ID, pasthistoryid: item._id },
      });
      dispatch(removePastHistoryItem(item._id));
      if (setmakeAction) setmakeAction(!makeAction);
    } catch (error) {
      console.error(error);
      toast.error("Failed to remove past history.");
    }
  };

  const getTypeLabel = (value: string) =>
    PAST_HISTORY_OPTIONS.find((o) => o.value === value)?.label ?? value;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-700">
        <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">
          Past History
        </h4>
      </div>

      <div className="p-4">
        <form
          onSubmit={handleAdd}
          className="flex flex-wrap gap-3 items-end mb-4"
        >
          <div className="w-40">
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-400 mb-1">
              Type *
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 rounded-lg border border-gray-600 dark:border-slate-600 focus:outline-none transition-all text-xs bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200"
            >
              <option value="">Select</option>
              {PAST_HISTORY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-400 mb-1">
              Note
            </label>
            <input
              type="text"
              name="note"
              value={formData.note}
              onChange={handleChange}
              placeholder="Enter note"
              className="w-full px-3 py-2 rounded-lg border border-gray-600 dark:border-slate-600 focus:outline-none transition-all text-xs bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200 placeholder-slate-500 dark:placeholder-slate-400"
            />
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-medium text-xs hover:shadow-lg hover:shadow-primary/20 transition-all disabled:opacity-50"
          >
            {isSubmitting ? "Adding..." : "Add"}
          </button>
        </form>

        {pastHistory.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">
                    Type
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">
                    Note
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase w-20">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {pastHistory.map((item: any) => (
                  <tr
                    key={item._id}
                    className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                  >
                    <td className="px-3 py-2 text-xs font-medium text-slate-800 dark:text-slate-200">
                      {getTypeLabel(item.type)}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">
                      {item.note || "-"}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => handleRemove(item)}
                        className="px-2.5 py-1 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 text-xs font-medium transition-colors"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pastHistory.length === 0 && (
          <div className="text-center py-4 text-slate-400 dark:text-slate-500">
            <p className="text-xs">No past history added yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default PastHistory;
